import { createContext, useContext, useReducer, type Dispatch } from 'react';

import { type CollapsiblePanelNames } from '../../queries/schema/carFinance.schema';
import type {
  PanelAttributes,
  PanelProviderProps,
  CollapsiblePanels,
} from './PanelContext.typed';

import { findNextInvalidPanelKey } from '../utils/findNextInvalidPanelKey/findNextInvalidPanelKey';

type Action =
  | { type: 'TOGGLE_PANEL'; panel: CollapsiblePanelNames }
  | { type: 'OPEN_PANEL'; panel: CollapsiblePanelNames }
  | { type: 'OPEN_PANELS'; panels: Array<CollapsiblePanelNames> }
  | { type: 'VALIDATE_PANEL'; panel: CollapsiblePanelNames; isValid: boolean }
  | { type: 'INVALIDATE_PANEL'; panel: CollapsiblePanelNames };

const PANEL_ORDER = [
  'personalDetails',
  'currentAddress',
  'employmentDetails',
  'bankingDetails',
  'consent',
] satisfies readonly CollapsiblePanelNames[];

const initialState: CollapsiblePanels = Object.fromEntries(
  PANEL_ORDER.map((name, i) => [name, { isOpen: i === 0, isValid: false }]),
) as CollapsiblePanels;

const panelReducer = (state: CollapsiblePanels, action: Action) => {
  switch (action.type) {
    case 'TOGGLE_PANEL':
      return {
        ...state,
        [action.panel]: {
          ...state[action.panel],
          isOpen: !state[action.panel].isOpen,
        },
      };
    case 'OPEN_PANEL':
      return {
        ...state,
        [action.panel]: {
          ...state[action.panel],
          isOpen: true,
        },
      };
    case 'OPEN_PANELS': {
      const result = action.panels.reduce(
        (
          obj: { [key: string]: PanelAttributes },
          key: CollapsiblePanelNames,
        ) => {
          obj[key] = {
            ...state[key],
            isOpen: true,
          };
          return obj;
        },
        {},
      );
      return {
        ...state,
        ...result,
      };
    }
    case 'VALIDATE_PANEL': {
      const nextKey = findNextInvalidPanelKey(state, action.panel);

      return {
        ...state,
        [action.panel]: {
          ...state[action.panel],
          isValid: action.isValid,
        },
        ...(nextKey
          ? {
              [nextKey]: {
                ...state[nextKey],
                isOpen: true,
              },
            }
          : {}),
      };
    }
    case 'INVALIDATE_PANEL':
      return {
        ...state,
        [action.panel]: {
          ...state[action.panel],
          isValid: false,
        },
      };
    default:
      return state;
  }
};

const PanelStateContext = createContext<CollapsiblePanels | null>(null);
const PanelDispatchContext = createContext<Dispatch<Action> | null>(null);

/**
 * This function is responsible for managing the open and closed states of the collapisble panels
 * within the car finance form, along with the state determining whether the panel is valid or invalid,
 * or complete or incomplete
 */
const PanelProvider = ({ children }: PanelProviderProps) => {
  const [state, dispatch] = useReducer(panelReducer, initialState);

  return (
    <PanelStateContext.Provider value={state}>
      <PanelDispatchContext.Provider value={dispatch}>
        {children}
      </PanelDispatchContext.Provider>
    </PanelStateContext.Provider>
  );
};

const usePanelState = () => {
  const context = useContext(PanelStateContext);
  if (!context) {
    throw new Error('usePanelState must be used within a PanelProvider');
  }
  return context;
};

const usePanelDispatch = () => {
  const context = useContext(PanelDispatchContext);
  if (!context) {
    throw new Error('usePanelDispatch must be used within a PanelProvider');
  }
  return context;
};

export { PanelProvider, usePanelState, usePanelDispatch, PANEL_ORDER };
